import { useEffect, useRef } from "react";
import { gsap } from "@/animations/gsap";
import { createTextReveal } from "@/animations/textReveal";
import { createFadeUp, createStaggerReveal } from "@/animations/reveal";
import { profile } from "@/data/profile";
import { PhotoGallery } from "@/components/Photos/PhotoGallery";

const FACTS: Array<[string, string]> = [
  ["ROLE", "C# / .NET TEAM LEAD"],
  ["EXPERIENCE", profile.experience],
  ["LOCATION", "INDIA"],
  ["FOCUS", ".NET · AI · VISION"],
];

export function About() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      createTextReveal(".about-heading .mask-line > span", ref.current!);
      createTextReveal(".about-statement .mask-line > span", ref.current!);
      createFadeUp(".about-body", ref.current!);
      createStaggerReveal(".about-fact", ref.current!);
      gsap.fromTo(
        ".about-rule",
        { scaleX: 0 },
        {
          scaleX: 1,
          transformOrigin: "left center",
          ease: "none",
          scrollTrigger: { trigger: ref.current, start: "top 75%", end: "center 45%", scrub: true },
        }
      );
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section id="about" ref={ref} className="container-x section-y" data-testid="about-section">
      <p className="eyebrow">01 — ABOUT</p>
      <h2 className="about-heading font-display mt-8 text-[clamp(2.5rem,6vw,5.5rem)] font-semibold uppercase leading-[0.98] tracking-tight">
        <span className="mask-line">
          <span>ENGINEERING</span>
        </span>
        <span className="mask-line">
          <span className="text-outline">MINDSET</span>
        </span>
      </h2>

      <div className="mt-16 grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className="about-statement font-display text-[clamp(1.4rem,2.4vw,2.1rem)] font-medium leading-snug" data-testid="about-statement">
            {profile.statement.map((line, i) => (
              <span className="mask-line" key={line}>
                <span className={i === profile.statement.length - 1 ? "text-accent" : undefined}>{line}</span>
              </span>
            ))}
          </p>
          <p className="about-body mt-8 max-w-xl text-base leading-relaxed text-muted">{profile.about}</p>
        </div>
        <div className="lg:col-span-4 lg:col-start-9">
          <dl className="grid gap-5">
            {FACTS.map(([key, value]) => (
              <div className="about-fact flex items-baseline justify-between gap-6 border-b border-line pb-4" key={key} data-testid={`about-fact-${key.toLowerCase()}`}>
                <dt className="font-mono text-[10px] tracking-[0.25em] text-muted">{key}</dt>
                <dd className="text-right font-mono text-xs tracking-wider">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="about-rule mt-20 h-px w-full bg-line" aria-hidden="true" />
      <PhotoGallery />
    </section>
  );
}
